import {computed, Directive, ElementRef, forwardRef, inject, input, Renderer2} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {FigureKind, parseFigure, sanitizeFigureText} from './figure-text';

@Directive({
  selector: 'input[appFigureInput]',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FigureInputDirective),
      multi: true,
    },
  ],
  host: {
    '(input)': 'onInput()',
    '(blur)': 'onBlur()',
    '[attr.inputmode]': 'inputMode()',
    autocomplete: 'off',
  },
})
export class FigureInputDirective implements ControlValueAccessor {
  private readonly elementRef = inject<ElementRef<HTMLInputElement>>(ElementRef);
  private readonly renderer = inject(Renderer2);

  readonly appFigureInput = input.required<FigureKind>();

  protected readonly inputMode = computed(() =>
    this.appFigureInput() === 'headcount' ? 'numeric' : 'decimal',
  );

  private onChange: (value: number | null) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  writeValue(value: number | null | undefined): void {
    this.setText(value === null || value === undefined ? '' : String(value));
  }

  registerOnChange(onChange: (value: number | null) => void): void {
    this.onChange = onChange;
  }

  registerOnTouched(onTouched: () => void): void {
    this.onTouched = onTouched;
  }

  setDisabledState(isDisabled: boolean): void {
    this.renderer.setProperty(this.elementRef.nativeElement, 'disabled', isDisabled);
  }

  protected onInput(): void {
    const rawText = this.elementRef.nativeElement.value;
    const sanitizedText = sanitizeFigureText(rawText, this.appFigureInput());
    if (sanitizedText !== rawText) {
      this.setText(sanitizedText);
    }
    this.onChange(parseFigure(sanitizedText));
  }

  protected onBlur(): void {
    this.onTouched();
  }

  private setText(text: string): void {
    this.renderer.setProperty(this.elementRef.nativeElement, 'value', text);
  }
}
